/**
 * Stable Event System API Service
 * - Tenant ID validation/sanitization
 * - Circuit breaker integration (optional)
 * - 10s timeout guard
 * - Cancellation tokens for consumers to cancel/ignore results
 * - Response validation and consistent fallbacks
 */
import { get, post, put, del } from './apiService';
import { API_ROUTES } from '../shared/apiRoutes/apiRoutes';
import { eventSystemBreakers } from '../utils/circuitBreakerStable';

// Sanitize a tenant ID to a safe string or null
export const sanitizeTenantId = (tenantId) => {
  if (tenantId == null) return null;
  const str = typeof tenantId === 'string' ? tenantId : String(tenantId);
  const trimmed = str.trim();
  return trimmed.length ? trimmed : null;
};

// Build URL with tenantId and extra params
const withTenantParams = (baseUrl, tenantId, extra = {}) => {
  const params = new URLSearchParams();
  const safeId = sanitizeTenantId(tenantId);
  if (safeId) params.set('tenantId', safeId);
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined && v !== null) params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${baseUrl}?${qs}` : baseUrl;
};

// Cancellation token factory
export const createCancelToken = () => ({ cancelled: false });

// Promise timeout wrapper (does not abort fetch, but guards callers)
const withTimeout = async (promise, ms = 10_000) => {
  let timerId;
  const timer = new Promise((_, reject) => {
    timerId = setTimeout(() => {
      const e = new Error('Request timed out');
      e.code = 'ETIMEDOUT';
      reject(e);
    }, ms);
  });
  try {
    return await Promise.race([promise, timer]);
  } finally {
    clearTimeout(timerId);
  }
};

// Normalize array responses
const toArray = (resp) => {
  if (!resp) return [];
  const data = resp.data ?? resp;
  return Array.isArray(data) ? data : [];
};

// Execute with optional circuit breaker, timeout, and cancellation
const execute = async (fn, { breaker, breakerKey, timeoutMs = 10_000, cancelToken } = {}) => {
  const run = () => (breaker ? breaker.execute(fn, breakerKey) : fn());
  const p = withTimeout(run(), timeoutMs);
  const result = await p;
  if (cancelToken?.cancelled) throw Object.assign(new Error('Request cancelled'), { code: 'ECANCEL' });
  return result;
};

// Devices
export const getDevicesStable = async (tenantId, { cancelToken, useBreaker = true } = {}) => {
  const url = withTenantParams(API_ROUTES.DEVICES, tenantId);
  try {
    const resp = await execute(
      () => get(url),
      {
        breaker: useBreaker ? eventSystemBreakers?.devices : null,
        breakerKey: `devices-${sanitizeTenantId(tenantId) || 'none'}`,
        timeoutMs: 10_000,
        cancelToken,
      }
    );
    return toArray(resp);
  } catch (err) {
    // Return safe fallback but keep logging
    console.error('getDevicesStable failed:', err);
    return [];
  }
};

// Actions
export const getActionsStable = async (tenantId, { cancelToken, useBreaker = true } = {}) => {
  const url = withTenantParams(API_ROUTES.ACTIONS, tenantId);
  try {
    const resp = await execute(
      () => get(url),
      {
        breaker: useBreaker ? eventSystemBreakers?.actions : null,
        breakerKey: `actions-${sanitizeTenantId(tenantId) || 'none'}`,
        timeoutMs: 10_000,
        cancelToken,
      }
    );
    return toArray(resp);
  } catch (err) {
    console.error('getActionsStable failed:', err);
    return [];
  }
};

// Flows
export const getFlowsStable = async (tenantId, { cancelToken, useBreaker = true, capability } = {}) => {
  const extra = {};
  if (capability) extra.capability = capability;
  const url = withTenantParams(API_ROUTES.FLOWS, tenantId, extra);
  try {
    const resp = await execute(
      () => get(url),
      {
        breaker: useBreaker ? eventSystemBreakers?.flows : null,
        breakerKey: `flows-${sanitizeTenantId(tenantId) || 'none'}`,
        timeoutMs: 10_000,
        cancelToken,
      }
    );
    return toArray(resp);
  } catch (err) {
    console.error('getFlowsStable failed:', err);
    return [];
  }
};

// Aggregated helper
export const getAllStable = async (tenantId, opts = {}) => {
  const [devices, actions, flows] = await Promise.all([
    getDevicesStable(tenantId, opts),
    getActionsStable(tenantId, opts),
    getFlowsStable(tenantId, opts),
  ]);
  return { devices, actions, flows };
};

// Namespaced export
export const eventSystemApiStable = {
  getDevices: getDevicesStable,
  getActions: getActionsStable,
  getFlows: getFlowsStable,
  getAll: getAllStable,
};

